import PropTypes from 'prop-types';
import React from 'react';
import injectSheet from 'react-jss';

import { approved, black2, rejected } from '~/assets/shared-styles/general';
import ApproveIcon from '~/components/Shared/Icons/ApproveIcon';
import InfoIcon from '~/components/Shared/Icons/InfoIcon';
import RejectIcon from '~/components/Shared/Icons/RejectIcon';
import { FormattedMessage, useIntl } from 'react-intl';
import { replaceAllSpaces } from '~/utils/strings';

import PasswordInputField from '../PasswordInputField/PasswordInputField';
import styles from './LoginPassword.styles';

function LoginPassword({
  classes,
  isWorkdayUser,
  passwordLabel,
  confirmPasswordLabel,
  password,
  confirmPassword,
  setPassword,
  setConfirmPassword,
  validationResult,
}) {
  const intl = useIntl();

  const passwordLabelDefault = intl.formatMessage({
    id: 'Deprecated_LoginPassword.passwordLabel',
    defaultMessage: 'New password',
    description: 'Label for new password input',
  });

  const confirmPasswordLabelDefault = intl.formatMessage({
    id: 'Deprecated_LoginPassword.confirmPasswordLabel',
    defaultMessage: 'Re-enter new password',
    description: 'Label for confirm password input',
  });

  const setPasswordChange = (password) => {
    const trimmedPw = replaceAllSpaces(password);
    setPassword(trimmedPw);
  };

  const setConfirmPasswordChange = (confirmedPassword) => {
    const trimmedConfirmedPassword = replaceAllSpaces(confirmedPassword);
    setConfirmPassword(trimmedConfirmedPassword);
  };

  const iconDisplay = (valid, value) => {
    return (
      <>
        {valid ? (
          <ApproveIcon color={approved} size="16" />
        ) : (
          <RejectIcon color={value ? rejected : black2} size="16" />
        )}
      </>
    );
  };

  return (
    <div>
      <div>
        <div className={classes.inputContainer}>
          <PasswordInputField
            disabled={isWorkdayUser}
            id="password"
            key="passwordfield"
            label={passwordLabel ?? passwordLabelDefault}
            value={password}
            onChange={setPasswordChange}
          />
        </div>

        <div className={classes.passwordValidation}>
          <div className={classes.validationEntity}>
            {iconDisplay(
              validationResult.validations.satisfiesMinimumCharacters,
              password,
            )}
            <FormattedMessage
              id="Deprecated_LoginPassword.minimumCharacters"
              defaultMessage="Minimum of 15 characters"
              description="Password validation: minimum characters"
            />
          </div>
          <div className={classes.validationEntity}>
            {iconDisplay(
              validationResult.validations.satisfiesCapitalCharacter,
              password,
            )}
            <FormattedMessage
              id="Deprecated_LoginPassword.capitalLetter"
              defaultMessage="At least one capital letter (A-Z)"
              description="Password validation: capital letter"
            />
          </div>
          <div className={classes.validationEntity}>
            {iconDisplay(
              validationResult.validations.satisfiesLowercaseCharacter,
              password,
            )}
            <FormattedMessage
              id="Deprecated_LoginPassword.lowercaseLetter"
              defaultMessage="At least one lowercase letter (a-z)"
              description="Password validation: lowercase letter"
            />
          </div>
          <div className={classes.validationEntity}>
            {iconDisplay(
              validationResult.validations.satisfiesNumberCharacter,
              password,
            )}
            <FormattedMessage
              id="Deprecated_LoginPassword.number"
              defaultMessage="At least one number (0-9)"
              description="Password validation: number"
            />
          </div>
          <div className={classes.validationEntity}>
            {iconDisplay(
              validationResult.validations.satisfiesSpecialCharacter,
              password,
            )}
            <FormattedMessage
              id="Deprecated_LoginPassword.specialCharacter"
              defaultMessage="At least one special character"
              description="Password validation: special character"
            />
          </div>
          <div className={classes.validationEntity}>
            {iconDisplay(
              validationResult.validations.satisfiesNoIdenticalRun,
              password,
            )}
            <FormattedMessage
              id="Deprecated_LoginPassword.noIdenticalRun"
              defaultMessage="No more than 3 identical characters in a row"
              description="Password validation: no identical run"
            />
          </div>
          <div className={classes.validationEntity}>
            {iconDisplay(
              validationResult.validations.satisfiesNoSequentialRun,
              password,
            )}
            <FormattedMessage
              id="Deprecated_LoginPassword.noSequentialRun"
              defaultMessage="No more than 3 consecutive characters in a sequence"
              description="Password validation: no sequential run"
            />
          </div>
          <div className={classes.validationEntity}>
            <span className={classes.iconAlignTop}>
              <InfoIcon size="16" color={black2} />
            </span>
            <FormattedMessage
              id="Deprecated_LoginPassword.noGuessableInfo"
              defaultMessage="Password cannot contain something easily guessable like your name, email address, or company name"
              description="Password validation: no guessable information"
            />
          </div>
        </div>
      </div>
      <div
        className={
          validationResult.isPasswordValid
            ? classes.formRow
            : classes.formRowDisabled
        }
      >
        <div className={classes.inputContainer}>
          <PasswordInputField
            disabled={isWorkdayUser || !validationResult.isPasswordValid}
            id="confirmedPassword"
            key="confirmpasswordfield"
            label={confirmPasswordLabel ?? confirmPasswordLabelDefault}
            value={confirmPassword}
            onChange={setConfirmPasswordChange}
          />
        </div>
        <div className={classes.validationEntity}>
          {iconDisplay(validationResult.isMatching, confirmPassword)}
          <FormattedMessage
            id="Deprecated_LoginPassword.passwordsMatch"
            defaultMessage="Passwords match"
            description="Password validation: passwords match"
          />
        </div>
      </div>
    </div>
  );
}

LoginPassword.propTypes = {
  isWorkdayUser: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  passPasswords: PropTypes.func.isRequired,
  setValidMatchPassword: PropTypes.func.isRequired,
};

export default injectSheet(styles)(LoginPassword);
